/**
 * What partner/getGcsImage hands back for a stored document link.
 *
 * The documented shape is `{ image_url }` - a short-lived signed GCS URL. The
 * string form and the base64 branch below are tolerated as well, so a plain
 * URL or an inlined image would still render rather than failing silently.
 */
export type GcsImageResponse =
  | string
  | {
      image_url?: string;
      content_type?: string;
    }
  | null;

/**
 * Turns a getGcsImage response into something usable as an `<img src>`.
 *
 * Returns an empty string when there is nothing displayable, so callers can
 * show an error state instead of a broken image.
 */
export function toDisplayableImageSrc(response: GcsImageResponse): string {
  const raw = extractString(response);
  if (!raw) return '';
  if (raw.startsWith('data:') || /^https?:\/\//i.test(raw)) return raw;
  const contentType =
    (typeof response === 'object' && response?.content_type) || 'image/jpeg';
  return `data:${contentType};base64,${raw}`;
}

function extractString(response: GcsImageResponse): string {
  if (!response) return '';
  if (typeof response === 'string') return response.trim();
  return response.image_url?.trim() ?? '';
}
